import React from 'react';
import { Link } from 'react-router-dom';
import { FaTruck, FaPlane, FaShippingFast, FaWarehouse, FaBoxes, FaPallet, FaThermometerHalf, FaUsers, FaHome, FaShoppingCart, FaClock, FaGlobe } from 'react-icons/fa';
import './Services.css';

function Services() {
  const services = [
    {
      icon: <FaShippingFast />,
      title: 'Same Day Delivery',
      description: 'Urgent delivery with immediate collection. We ensure your goods are picked up within the same day and delivered directly with no delays or co-loading. Perfect for time-critical shipments that can\'t wait.',
      features: ['Immediate collection', 'Same-day delivery guaranteed', 'Direct delivery - no co-loading', 'Real-time tracking']
    },
    {
      icon: <FaTruck />,
      title: 'Nationwide On-Call Collection & Delivery',
      description: 'We operate across the UK with drivers ready to respond instantly. One call is all it takes for rapid collection and direct delivery to any destination nationwide. Available 24/7 for your convenience.',
      features: ['UK-wide coverage', 'Instant response', 'Direct delivery', '24/7 availability']
    },
    {
      icon: <FaClock />,
      title: 'Half Day & Full Day Hire',
      description: 'Flexible and cost-effective vehicle hire with driver for your operational needs. Choose half-day or full-day support to handle multiple deliveries efficiently. Ideal for businesses with varying daily requirements.',
      features: ['Professional driver included', 'Flexible duration', 'Multiple stops', 'Cost-effective solution']
    },
    {
      icon: <FaGlobe />,
      title: 'Import & Export Logistics',
      description: 'Reliable and secure international freight handling via air, sea, and road networks. We manage documentation, customs clearance, and transit coordination for hassle-free international shipping.',
      features: ['Air, sea & road freight', 'Customs clearance assistance', 'Documentation handling', 'Transit coordination']
    },
    {
      icon: <FaBoxes />,
      title: 'Multi-Drop Delivery',
      description: 'Efficient delivery routes planned to handle multiple drops in one journey. Ideal for retail, wholesale, and scheduled distribution needs. Optimize your delivery costs with our smart routing.',
      features: ['Optimized routing', 'Multiple stops', 'Scheduled deliveries', 'Perfect for retail/wholesale']
    },
    {
      icon: <FaPlane />,
      title: 'European Road Transport',
      description: 'Door-to-door road freight solutions across Europe. Perfect for pallet deliveries, commercial goods, and time-sensitive shipments. Reliable service with experienced European transport specialists.',
      features: ['Pan-European coverage', 'Door-to-door service', 'Pallet & commercial freight', 'Experienced drivers']
    },
    {
      icon: <FaThermometerHalf />,
      title: 'Temperature Controlled Transport',
      description: 'Vehicles equipped for chilled, frozen, or ambient-sensitive goods. Ideal for pharmaceuticals, food products, and medical supplies requiring specific temperature conditions throughout transit.',
      features: ['Chilled & frozen capability', 'Temperature monitoring', 'Pharmaceutical compliant', 'Medical supplies specialist']
    },
    {
      icon: <FaBoxes />,
      title: 'Parcel Delivery',
      description: 'Fast, secure, and trackable parcel services for personal and business needs. Collections available at short notice with door-to-door delivery and proof of delivery.',
      features: ['Fast & secure', 'Short notice collection', 'Door-to-door service', 'Proof of delivery']
    },
    {
      icon: <FaUsers />,
      title: 'Two-Man Delivery',
      description: 'Specialist two-person teams for heavy, large, or fragile items. Safe handling, lifting, and placement exactly where the customer needs. Professional service for items requiring extra care.',
      features: ['Professional handling', 'Heavy item specialists', 'Precise placement', 'Fragile item care']
    },
    {
      icon: <FaPallet />,
      title: 'Full Load Transport',
      description: 'Dedicated full vehicle loads with no shared space. Ideal for large shipments requiring direct and uninterrupted transport. Your goods travel alone for maximum security and speed.',
      features: ['Dedicated vehicle', 'No co-loading', 'Direct transport', 'Volume discounts']
    },
    {
      icon: <FaShoppingCart />,
      title: 'E-Commerce Fulfillment',
      description: 'Complete pick, pack, and dispatch solutions designed to support online retailers. Real-time tracking and efficient order handling to keep your customers happy.',
      features: ['Pick & pack service', 'Order processing', 'Returns management', 'Platform integration']
    },
    {
      icon: <FaHome />,
      title: 'House Moving & Removals',
      description: 'Professional domestic removal service with experienced teams. We handle furniture, appliances, personal belongings, and full household moves with care. Stress-free moving solutions.',
      features: ['Full household moves', 'Furniture handling', 'Experienced team', 'Care & attention']
    }
  ];

  const fleet = [
    { 
      name: 'Small Van', 
      description: 'Ideal for small parcels and rapid local deliveries',
      image: '/images/Mercedes_Small_Van.png'
    },
    { 
      name: 'Medium Van', 
      description: 'Versatile vehicle suitable for everyday business transport',
      image: '/images/Medium_van.png'
    },
    { 
      name: 'Long Wheel Base Van', 
      description: 'Perfect for larger shipments and multiple-item deliveries',
      image: '/images/Long_Wheel_Base_Van.png'
    },
    { 
      name: 'Extra Long Wheel Base Van', 
      description: 'Increased storage volume for bulky or heavy goods',
      image: '/images/mercedes_Extra_Long_Wheel_Base_Van.png'
    },
    { 
      name: 'Luton Van', 
      description: 'Excellent for furniture, household moves, and fragile items',
      image: '/images/mercedes_Luton_Van.png'
    },
    { 
      name: 'Box & Curtain-Side Vans', 
      description: 'Suitable for palletised goods, commercial freight, and easy side-loading',
      image: '/images/mercedes_Box_Curtain_Side_Vans.png'
    }
  ];

  return (
    <main className="services-page">
      <section className="services-hero">
        <div className="container">
          <h1>Our Services</h1>
          <p>Comprehensive courier and logistics solutions across the UK and Europe</p>
        </div>
      </section>

      <section className="services-content">
        <div className="container">
          {services.map((service, index) => (
            <div className="service-detail" key={index}>
              <div className="service-detail-icon">
                {service.icon}
              </div>
              <div className="service-detail-content">
                <h2>{service.title}</h2>
                <p className="service-description">{service.description}</p>
                <ul className="service-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>✓ {feature}</li>
                  ))}
                </ul>
                <Link to="/quote" className="btn btn-primary">Get Quote</Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="fleet-section">
        <div className="container">
          <h2 className="section-title">Our Fleet</h2>
          <p className="section-subtitle">
            Wide range of vehicles to suit all delivery requirements, from single parcels to full commercial loads
          </p>
          <div className="fleet-grid">
            {fleet.map((vehicle, index) => (
              <div className="fleet-card" key={index}>
                <div className="fleet-image-wrapper">
                  {vehicle.image ? (
                    <img 
                      src={vehicle.image} 
                      alt={vehicle.name}
                      className="fleet-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="fleet-icon" style={{ display: vehicle.image ? 'none' : 'flex' }}>
                    <FaTruck />
                  </div>
                </div>
                <h3>{vehicle.name}</h3>
                <p>{vehicle.description}</p>
              </div>
            ))}
          </div>
          <div className="fleet-note">
            <p>Each vehicle is maintained to high safety and reliability standards, ensuring your goods travel securely at all times.</p>
          </div>
        </div>
      </section>

      <section className="service-benefits">
        <div className="container">
          <h2 className="section-title">Why Choose V&R Courier Ltd</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <h3>24/7 Availability</h3>
              <p>Always ready when you need us for urgent collections and deliveries.</p>
            </div>
            <div className="benefit-item">
              <h3>Nationwide Coverage</h3>
              <p>Reaching every corner of the UK, plus Europe-wide transport services.</p>
            </div>
            <div className="benefit-item">
              <h3>Dedicated Delivery</h3>
              <p>Your goods travel directly, with no co-loading for faster, safer delivery.</p>
            </div>
            <div className="benefit-item">
              <h3>Fast & Reliable</h3>
              <p>We prioritise timely deliveries every time with professional service.</p>
            </div>
            <div className="benefit-item">
              <h3>Real-Time Tracking</h3>
              <p>Know where your shipment is at every stage of the journey.</p>
            </div>
            <div className="benefit-item">
              <h3>Professional Team</h3>
              <p>Trained, uniformed and experienced drivers and handlers.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Ready to Get Started?</h2>
          <p>Contact us today for a customized shipping solution</p>
          <div className="cta-buttons">
            <Link to="/quote" className="btn btn-primary">Request Quote</Link>
            <Link to="/contact" className="btn btn-outline">Contact Us</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Services;
